import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Grid,
  Skeleton,
  TextField,
  Button,
  Stack
} from '@mui/material';
import { Link } from 'react-router-dom';

// MUI のアイコンインポート
import RateReviewIcon from '@mui/icons-material/RateReview';
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

import app from './firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  startAt,
  endAt,
  startAfter,
  getFirestore,
  limit as firebaseLimit,
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

const db = getFirestore(app);
const storage = getStorage(app);

// 個々のカード（サムネイル、タイトル、evaluations、points）表示コンポーネント
const PaperInfoCard = ({ paper }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [loadingThumb, setLoadingThumb] = useState(true);

  // Firebase Storage のパスからダウンロード URL を生成
  useEffect(() => {
    if (paper.thumbnailFilePath) {
      const storageRef = ref(storage, paper.thumbnailFilePath);
      getDownloadURL(storageRef)
        .then((url) => {
          setThumbnailUrl(url);
        })
        .catch((error) => {
          console.error('Error getting download URL:', error);
        })
        .finally(() => {
          setLoadingThumb(false);
        });
    } else {
      setLoadingThumb(false);
    }
  }, [paper.thumbnailFilePath]);

  return (
    <Card sx={{ maxWidth: 345, mx: 'auto' }}>
      {loadingThumb ? (
        <Skeleton
          variant="rectangular"
          sx={{
            height: 0,
            paddingTop: '66.66%', // 3:2 のアスペクト比を維持
          }}
        />
      ) : (
        thumbnailUrl && (
          <CardMedia
            component="img"
            image={thumbnailUrl}
            alt={paper.title}
            sx={{
              aspectRatio: '3 / 2',
              objectFit: 'cover',
            }}
          />
        )
      )}
      <CardContent>
        <Typography variant="h6" component="div" gutterBottom>
          {paper.title}
        </Typography>
        {/* evaluations と points をアイコンと共に表示 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <RateReviewIcon fontSize="small" />
            <Typography variant="body2">
              {paper.evaluations ?? 0}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WorkspacePremiumIcon fontSize="small" />
            <Typography variant="body2">
              {paper.points ?? 0}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// データ取得中のプレースホルダー用の Skeleton カード
const PaperInfoCardSkeleton = () => {
  return (
    <Card sx={{ maxWidth: 345, mx: 'auto' }}>
      <Skeleton
        variant="rectangular"
        sx={{
          height: 0,
          paddingTop: '66.66%', // 3:2 の比率
        }}
      />
      <CardContent>
        <Skeleton variant="text" height={30} width="80%" />
        {/* evaluations と points 用の Skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Skeleton variant="text" height={20} width={50} />
          <Skeleton variant="text" height={20} width={50} />
        </Box>
      </CardContent>
    </Card>
  );
};

function ExploreList() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  // 実際に検索に使う状態
  const [searchTerm, setSearchTerm] = useState('');
  // ユーザー入力中の状態（エンターキーで searchTerm に反映させる）
  const [pendingSearch, setPendingSearch] = useState('');
  // 現在のページ番号（0ページ目からスタート）
  const [currentPage, setCurrentPage] = useState(0);
  // 各ページの最後のドキュメント（次ページ取得時のカーソルとして利用）
  const [lastDoc, setLastDoc] = useState(null);
  // 次ページが存在するかのフラグ
  const [hasNextPage, setHasNextPage] = useState(false);

  const itemsPerPage = 30;

  // 検索文字列が変更されたら初期状態にリセット
  useEffect(() => {
    // 検索実行時は最初のページから取得
    setPapers([]);
    setCurrentPage(0);
    setLastDoc(null);
  }, [searchTerm]);

  // ページングおよび検索に応じたデータ取得処理
  useEffect(() => {
    async function fetchPapers() {
      setLoading(true);
      try {
        const baseRef = collection(db, 'papers');
        let q;
        if (searchTerm) {
          q = query(
            baseRef,
            orderBy('title'),
            startAt(searchTerm),
            endAt(searchTerm + '\uf8ff'),
            firebaseLimit(itemsPerPage)
          );
        } else {
          q = query(baseRef, orderBy('title'), firebaseLimit(itemsPerPage));
        }

        // ページが進んでいる場合はカーソルを利用して続きを取得
        if (lastDoc) {
          q = query(q, startAfter(lastDoc));
        }

        const snapshot = await getDocs(q);
        const docsData = [];
        snapshot.forEach((doc) => {
          docsData.push({ id: doc.id, ...doc.data() });
        });

        // 初回なら上書き、2回目以降なら既存のリストに追加
        if (currentPage === 0) {
          setPapers(docsData);
        } else {
          setPapers((prev) => [...prev, ...docsData]);
        }

        // 取得件数が itemsPerPage と同じなら次ページが存在する可能性あり
        setHasNextPage(snapshot.docs.length === itemsPerPage);
        if (snapshot.docs.length > 0) {
          // 次回取得用のカーソルとして最後のドキュメントを保存
          setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        }
      } catch (error) {
        console.error('Error fetching papers:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchPapers();
    // currentPage の変更により、lastDoc や searchTerm が更新されるので依存配列に含める
  }, [searchTerm, currentPage]);

  return (
    <Box sx={{ padding: 2 }}>
      {/* 検索欄 */}
      <Box sx={{ marginBottom: 2 }}>
        <TextField
          label="🔍Search"
          variant="outlined"
          fullWidth
          value={pendingSearch}
          onChange={(e) => setPendingSearch(e.target.value)}
          placeholder="Title"
          // エンターキーが押されたら searchTerm を更新して検索実行
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearchTerm(pendingSearch);
            }
          }}
        />
      </Box>

      {/* カードリスト */}
      <Grid container spacing={2}>
        {papers.map((paper) => (
          <Grid item key={paper.id} xs={12} sm={6} md={4} lg={3}>
            <Link
              to={`/paper/${paper.id}`}
              state={{ paper }}
              style={{ textDecoration: 'none' }}
            >
              <PaperInfoCard paper={paper} />
            </Link>
          </Grid>
        ))}
        {/* データ取得中は Skeleton を表示 */}
        {loading &&
          Array.from(new Array(8)).map((_, index) => (
            <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
              <PaperInfoCardSkeleton />
            </Grid>
          ))}
      </Grid>

      {/* 次ページが存在する場合は「もっと見る」ボタンを表示 */}
      {hasNextPage && !loading && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => setCurrentPage((prev) => prev + 1)}
            sx={{ maxWidth: 200 }}
          >
            MORE
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default ExploreList;

